import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Clock, Calendar, Bell, Lock, Unlock, Plus, Trash2, Save, FileText, Zap, Zap as Lightning, Lock as LockIcon, Unlock as UnlockIcon, FileText as FileTextIcon, CheckCircle } from 'lucide-react';

interface ReadingSession {
  id: string;
  title: string;
  duration: number; // in minutes
  priority: 'low' | 'medium' | 'high';
  category: string;
  estimatedReadingTime: number;
  scheduledTime?: Date;
  completed: boolean;
  completedAt?: Date;
  notes?: string;
}

interface OptimalTimeSlot {
  startTime: Date;
  endTime: Date;
  score: number; // 0-100, higher is better
  factors: {
    energyLevel: number;
    distractionLevel: number;
    historicalPerformance: number;
    personalPreference: number;
  };
}

interface SmartSchedulingProps {
  userId: string;
  onSessionScheduled?: (session: ReadingSession) => void;
  onReminderSet?: (reminder: { time: Date; message: string }) => void;
  onOptimalTimeFound?: (timeSlot: OptimalTimeSlot) => void;
}

const SmartScheduling: React.FC<SmartSchedulingProps> = ({
  userId,
  onSessionScheduled,
  onReminderSet,
  onOptimalTimeFound
}) => {
  const [sessions, setSessions] = useState<ReadingSession[]>([]);
  const [optimalTimeSlots, setOptimalTimeSlots] = useState<OptimalTimeSlot[]>([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<OptimalTimeSlot | null>(null);
  const [showScheduler, setShowScheduler] = useState(false);
  const [newSession, setNewSession] = useState<Partial<ReadingSession>>({
    title: '',
    duration: 30,
    priority: 'medium',
    category: 'general',
    estimatedReadingTime: 15
  });
  const [reminders, setReminders] = useState<Array<{ id: string; time: Date; message: string; active: boolean }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSessions();
    loadOptimalTimeSlots();
    loadReminders();
  }, [userId, selectedDate]);

  const loadSessions = async () => {
    try {
      setIsLoading(true);
      // In a real app, this would fetch from your backend
      const sampleSessions: ReadingSession[] = [
        {
          id: '1',
          title: 'Morning News Brief',
          duration: 15,
          priority: 'high',
          category: 'news',
          estimatedReadingTime: 10,
          scheduledTime: new Date(selectedDate.getTime() + 8 * 60 * 60 * 1000), // 8 AM
          completed: false
        },
        {
          id: '2',
          title: 'Technical Article Review',
          duration: 45,
          priority: 'medium',
          category: 'technical',
          estimatedReadingTime: 30,
          scheduledTime: new Date(selectedDate.getTime() + 14 * 60 * 60 * 1000), // 2 PM
          completed: false
        },
        {
          id: '3',
          title: 'Evening Learning Session',
          duration: 30,
          priority: 'low',
          category: 'learning',
          estimatedReadingTime: 25,
          scheduledTime: new Date(selectedDate.getTime() + 19 * 60 * 60 * 1000), // 7 PM
          completed: false
        }
      ];
      setSessions(sampleSessions);
    } catch (err) {
      setError('Failed to load sessions');
    } finally {
      setIsLoading(false);
    }
  };

  const loadOptimalTimeSlots = async () => {
    try {
      // In a real app, this would use AI to analyze user patterns
      const today = selectedDate;
      const slots: OptimalTimeSlot[] = [
        {
          startTime: new Date(today.getTime() + 8 * 60 * 60 * 1000), // 8 AM
          endTime: new Date(today.getTime() + 9 * 60 * 60 * 1000), // 9 AM
          score: 85,
          factors: {
            energyLevel: 90,
            distractionLevel: 20,
            historicalPerformance: 80,
            personalPreference: 85
          }
        },
        {
          startTime: new Date(today.getTime() + 14 * 60 * 60 * 1000), // 2 PM
          endTime: new Date(today.getTime() + 15 * 60 * 60 * 1000), // 3 PM
          score: 75,
          factors: {
            energyLevel: 70,
            distractionLevel: 40,
            historicalPerformance: 75,
            personalPreference: 70
          }
        },
        {
          startTime: new Date(today.getTime() + 20 * 60 * 60 * 1000), // 8 PM
          endTime: new Date(today.getTime() + 21 * 60 * 60 * 1000), // 9 PM
          score: 90,
          factors: {
            energyLevel: 85,
            distractionLevel: 10,
            historicalPerformance: 90,
            personalPreference: 95
          }
        }
      ];
      setOptimalTimeSlots(slots);
    } catch (err) {
      setError('Failed to load optimal time slots');
    }
  };

  const loadReminders = async () => {
    try {
      // In a real app, this would fetch from your backend
      const sampleReminders = [
        {
          id: '1',
          time: new Date(selectedDate.getTime() + 8 * 60 * 60 * 1000),
          message: 'Time for your morning reading session!',
          active: true
        },
        {
          id: '2',
          time: new Date(selectedDate.getTime() + 14 * 60 * 60 * 1000),
          message: 'Technical article review scheduled',
          active: true
        }
      ];
      setReminders(sampleReminders);
    } catch (err) {
      setError('Failed to load reminders');
    }
  };

  const handleScheduleSession = () => {
    if (!newSession.title || !selectedTimeSlot) return;

    const session: ReadingSession = {
      id: Date.now().toString(),
      title: newSession.title,
      duration: newSession.duration || 30,
      priority: newSession.priority || 'medium',
      category: newSession.category || 'general',
      estimatedReadingTime: newSession.estimatedReadingTime || 15,
      scheduledTime: selectedTimeSlot.startTime,
      completed: false
    };

    setSessions(prev => [...prev, session]);
    onSessionScheduled?.(session);
    
    // Reset form
    setNewSession({
      title: '',
      duration: 30,
      priority: 'medium',
      category: 'general',
      estimatedReadingTime: 15
    });
    setSelectedTimeSlot(null);
    setShowScheduler(false);
  };

  const handleSetReminder = (timeSlot: OptimalTimeSlot) => {
    const reminder = {
      id: Date.now().toString(),
      time: timeSlot.startTime,
      message: `Optimal reading time: ${timeSlot.startTime.toLocaleTimeString()}`,
      active: true
    };

    setReminders(prev => [...prev, reminder]);
    onReminderSet?.(reminder);
  };

  const handleCompleteSession = (sessionId: string) => {
    setSessions(prev => prev.map(session => 
      session.id === sessionId 
        ? { ...session, completed: true, completedAt: new Date() }
        : session
    ));
  };

  const handleDeleteSession = (sessionId: string) => {
    setSessions(prev => prev.filter(session => session.id !== sessionId));
  };

  const handleToggleReminder = (reminderId: string) => {
    setReminders(prev => prev.map(reminder => 
      reminder.id === reminderId 
        ? { ...reminder, active: !reminder.active }
        : reminder
    ));
  };

  const handleDeleteReminder = (reminderId: string) => {
    setReminders(prev => prev.filter(reminder => reminder.id !== reminderId));
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600 dark:text-red-400';
      case 'medium': return 'text-yellow-600 dark:text-yellow-400';
      case 'low': return 'text-green-600 dark:text-green-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getPriorityBgColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 dark:bg-red-900';
      case 'medium': return 'bg-yellow-100 dark:bg-yellow-900';
      case 'low': return 'bg-green-100 dark:bg-green-900';
      default: return 'bg-gray-100 dark:bg-gray-800';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 80) return 'bg-green-100 dark:bg-green-900';
    if (score >= 60) return 'bg-yellow-100 dark:bg-yellow-900';
    return 'bg-red-100 dark:bg-red-900';
  };

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString([], { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <div className="text-red-500 mb-4">{error}</div>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Smart Scheduling
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          AI-powered reading schedule optimization based on your habits and preferences
        </p>
      </div>

      {/* Date Selector */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {formatDate(selectedDate)}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Select a date to view optimal reading times
            </p>
          </div>
          <input
            type="date"
            value={selectedDate.toISOString().split('T')[0]}
            onChange={(e) => setSelectedDate(new Date(e.target.value))}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Optimal Time Slots */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          <Clock className="h-5 w-5 inline mr-2" />
          Optimal Reading Times
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {optimalTimeSlots.map((slot, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md ${
                selectedTimeSlot === slot 
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900' 
                  : 'border-gray-200 dark:border-gray-700'
              }`}
              onClick={() => setSelectedTimeSlot(slot)}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                </div>
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreBgColor(slot.score)} ${getScoreColor(slot.score)}`}>
                  {slot.score}%
                </div>
              </div>
              
              <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex justify-between">
                  <span>Energy Level:</span>
                  <span>{slot.factors.energyLevel}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Distraction Level:</span>
                  <span>{slot.factors.distractionLevel}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Historical Performance:</span>
                  <span>{slot.factors.historicalPerformance}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Personal Preference:</span>
                  <span>{slot.factors.personalPreference}%</span>
                </div>
              </div>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSetReminder(slot);
                }}
                className="mt-3 w-full px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
              >
                <Bell className="h-4 w-4 inline mr-1" />
                Set Reminder
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Schedule New Session */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            <Calendar className="h-5 w-5 inline mr-2" />
            Schedule Reading Session
          </h2>
          <button
            onClick={() => setShowScheduler(!showScheduler)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            <Plus className="h-4 w-4 inline mr-1" />
            {showScheduler ? 'Cancel' : 'New Session'}
          </button>
        </div>

        {showScheduler && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Session Title
                </label>
                <input
                  type="text"
                  value={newSession.title || ''}
                  onChange={(e) => setNewSession(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter session title"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Duration (minutes)
                </label>
                <input
                  type="number"
                  value={newSession.duration || 30}
                  onChange={(e) => setNewSession(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="5"
                  max="120"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Priority
                </label>
                <select
                  value={newSession.priority || 'medium'}
                  onChange={(e) => setNewSession(prev => ({ ...prev, priority: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category
                </label>
                <select
                  value={newSession.category || 'general'}
                  onChange={(e) => setNewSession(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="general">General</option>
                  <option value="news">News</option>
                  <option value="technical">Technical</option>
                  <option value="learning">Learning</option>
                  <option value="entertainment">Entertainment</option>
                </select>
              </div>
            </div>
            
            {selectedTimeSlot && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900 rounded-lg">
                <p className="text-blue-900 dark:text-blue-100">
                  <Clock className="h-4 w-4 inline mr-1" />
                  Scheduled for: {formatTime(selectedTimeSlot.startTime)} - {formatTime(selectedTimeSlot.endTime)}
                </p>
              </div>
            )}
            
            <div className="flex gap-4">
              <button
                onClick={handleScheduleSession}
                disabled={!newSession.title || !selectedTimeSlot}
                className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="h-4 w-4 inline mr-1" />
                Schedule Session
              </button>
              <button
                onClick={() => setShowScheduler(false)}
                className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Scheduled Sessions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          <Calendar className="h-5 w-5 inline mr-2" />
          Scheduled Sessions
        </h2>
        
        <div className="space-y-4">
          {sessions.map((session) => (
            <div
              key={session.id}
              className={`p-4 rounded-lg border ${
                session.completed 
                  ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900' 
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {session.title}
                    </h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityBgColor(session.priority)} ${getPriorityColor(session.priority)}`}>
                      {session.priority}
                    </span>
                    {session.completed && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400">
                        Completed
                      </span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 dark:text-gray-400">
                    <div>
                      <span className="font-medium">Time:</span> {session.scheduledTime ? formatTime(session.scheduledTime) : 'Not scheduled'}
                    </div>
                    <div>
                      <span className="font-medium">Duration:</span> {session.duration} min
                    </div>
                    <div>
                      <span className="font-medium">Category:</span> {session.category}
                    </div>
                    <div>
                      <span className="font-medium">Estimated:</span> {session.estimatedReadingTime} min
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  {!session.completed && (
                    <button
                      onClick={() => handleCompleteSession(session.id)}
                      className="px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm"
                    >
                      <CheckCircle className="h-4 w-4 inline mr-1" />
                      Complete
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteSession(session.id)}
                    className="px-3 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm"
                  >
                    <Trash2 className="h-4 w-4 inline mr-1" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {sessions.length === 0 && (
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No sessions scheduled
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Schedule your first reading session to get started
            </p>
          </div>
        )}
      </div>

      {/* Reminders */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          <Bell className="h-5 w-5 inline mr-2" />
          Reminders
        </h2>
        
        <div className="space-y-3">
          {reminders.map((reminder) => (
            <div
              key={reminder.id}
              className={`p-3 rounded-lg border ${
                reminder.active 
                  ? 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900' 
                  : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatTime(reminder.time)}
                    </span>
                    {!reminder.active && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                        Inactive
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {reminder.message}
                  </p>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => handleToggleReminder(reminder.id)}
                    className={`px-3 py-1 rounded-lg text-sm ${
                      reminder.active 
                        ? 'bg-yellow-500 text-white hover:bg-yellow-600' 
                        : 'bg-green-500 text-white hover:bg-green-600'
                    }`}
                  >
                    {reminder.active ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    onClick={() => handleDeleteReminder(reminder.id)}
                    className="px-3 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm"
                  >
                    <Trash2 className="h-4 w-4 inline mr-1" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {reminders.length === 0 && (
          <div className="text-center py-8">
            <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No reminders set
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Set reminders for optimal reading times
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SmartScheduling;